import * as React from 'react';
import { useLocation, useRoute } from 'wouter';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { CmdTable } from '../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { CUSTOMER_KEY, REQUIRED } from '../CmdTable/constants';
import * as CmdTableService from '../CmdTable/CmdTableService';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import PermissionsService, { PERMISSION_COST_CENTERS } from '../../Services/PermissionsService';
import { URL_CODEBOOKS, URL_COST_CENTERS } from '../../router/constants';
import { customersAtom, selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';
import useConstCenters from './useCostCenters';
import {
  COST_CENTERS_DELETE_KEY,
  COST_CENTERS_DETAIL_KEY,
  COST_CENTERS_NAME_KEY,
  COST_CENTERS_TABLE_KEY,
  COST_CENTERS_TABLE_SETTINGS,
} from './constants';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import { ConstCenterRecord, CostCenterTableSettingsKeys } from './types';
import Form from './Form';
import {
  TABLE_CODEBOOKS_PARKING,
  TABLE_CODEBOOKS_RIDE,
  TABLE_COST_CENTERS,
} from '../../Services/TableService';
import TabComponent from '../TabComponent';
import { tabsByPermission } from '../Codebooks/CodeBooksService';

export default function CostCenters() {
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [pathname, navigate] = useLocation();
  const selectedCustomers = useRecoilValue(selectedCustomersAtom);
  const query = useUrlQueryParams();
  const [, params] = useRoute(`${URL_CODEBOOKS}${URL_COST_CENTERS}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(COST_CENTERS_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(COST_CENTERS_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(COST_CENTERS_TABLE_KEY)
  );
  const customerIdUrl = query.get('customerId');
  const { columns, columnOrder, columnVisibility } = useConstCenters();

  const handleClose = React.useCallback(() => {
    navigate(`${URL_CODEBOOKS}${URL_COST_CENTERS}`);
    setIsModalOpened(false);
  }, [navigate]);

  const tabs = React.useState(
    tabsByPermission(
      [TABLE_CODEBOOKS_RIDE, TABLE_CODEBOOKS_PARKING, TABLE_COST_CENTERS],
      TABLE_COST_CENTERS,
      selectedCustomers
    )
  )[0];

  const handlePathData = (
    documentTitle: string,
    customerValue: string | null,
    modalOpened: boolean
  ) => {
    setCustomerId(customerValue ? Number(customerValue) : null);
    setDocumentTitle(documentTitle);
    setIsModalOpened(modalOpened);
  };

  React.useEffect(() => {
    if (pathname === `${URL_CODEBOOKS}${URL_COST_CENTERS}`) {
      const documentTitle = `${f({ id: 'tab.cost-centers' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      handlePathData(documentTitle, null, false);
    } else if (pathname === `${URL_CODEBOOKS}${URL_COST_CENTERS}/add`) {
      const documentTitle = `${f({ id: 'table.add' })} - ${f({ id: 'tab.cost-centers' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      handlePathData(documentTitle, null, true);
    } else if (params) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({ id: 'tab.cost-centers' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      handlePathData(documentTitle, customerIdUrl, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, pathname, params, query]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMER_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
          system: true,
        });
      } else if (item.name === CUSTOMER_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: true,
          hidden: false,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = CmdTableService.arrayOrder(
      [...tempTableSettingOrder],
      [...tempOrder],
      'key'
    );

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await CmdTableService.getTableSettings(COST_CENTERS_TABLE_KEY);
    if (settings) {
      handleSetOrderColumns(settings.columns);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [`cmdTableSettings-${TABLE_COST_CENTERS}`, customer],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  const handleAddButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate(`${URL_CODEBOOKS}${URL_COST_CENTERS}/add`);
  };

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [COST_CENTERS_TABLE_KEY, filterForRequest, sortFilter],
    });
  };

  return (
    <div>
      <div className="w-page">
        <div className="w-page-content">
          <div className="page-header">
            <h4>
              {`${f({ id: 'menu.settings' })} / ${f({ id: 'table.systemSettings' })}`}
              <OnlineHelp topic="codebooks" />
            </h4>
          </div>
          <div className="container-fluid">
            <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} />
            <section className="basic-box border-top-thin border-top-gray">
              <div className="basic-box__content">
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {PermissionsService.hasAccess(
                      PERMISSION_COST_CENTERS,
                      ['create'],
                      selectedCustomers
                    ) && (
                      <button
                        type="button"
                        data-cy="table-record-addBtn"
                        title={f({ id: 'table.addBtn' })}
                        className="e-button e-button--gray"
                        data-id="addAlarm"
                        onClick={handleAddButton}
                      >
                        {f({ id: 'table.addBtn' })}
                      </button>
                    )}
                    {/* </div> */}
                  </div>
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={`${COST_CENTERS_TABLE_KEY}`}
                      actionColumnEnabled={true}
                      tableName={COST_CENTERS_TABLE_SETTINGS}
                      sort={{ column: COST_CENTERS_NAME_KEY, direction: 'asc' }}
                      translationKeys={CostCenterTableSettingsKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips
                        key={filterId}
                        id={filterId}
                        tableName={COST_CENTERS_TABLE_KEY}
                      />
                    ))}
                </div>
                {isLoadingSettings && <CmdLoader inContent={true} />}
                <CmdTable<any, ConstCenterRecord>
                  name={COST_CENTERS_TABLE_KEY}
                  stickyColumns={[COST_CENTERS_DETAIL_KEY, COST_CENTERS_DELETE_KEY]}
                  lockedColumns={[COST_CENTERS_NAME_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
            {isModalOpened && (
              <div>
                {PermissionsService.hasAccess(
                  PERMISSION_COST_CENTERS,
                  params ? ['read', 'update'] : ['create'],
                  customerId
                ) && (
                  <Form
                    id={params ? Number(params.id) : null}
                    customerId={customerId}
                    updateTable={updateTable}
                    handleClose={handleClose}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
